const SUMMARIZE_PREFIXES = [
  'Revenue',
  'EBITDA',
  'Gross Profit',
  'Gross Margin',
  'Volatility',
  'Market Cap',
  'Implied',
  'Enterprise Value',
  'Total',
];

const MULTIPLE_PREFIXES = ['Revenue', 'EBITDA', 'Gross Profit', 'Gross Margin'];
const INDUSTRY_PREFIXES = ['Volatility', 'Total'];
const VALUE_PREFIXES = ['Market Cap', 'Enterprise Value'];

const SUMMARY_ROW_KEYS = [
  'max',
  '95%',
  '90%',
  '75%',
  '50%',
  '25%',
  '10%',
  '5%',
  'min',
  'mean',
];

function startsWithAny(key, prefixes) {
  // Match backend: "Market Cap" must also match "Market Capitalization | ..."
  return prefixes.some((prefix) => key.startsWith(prefix));
}

function extractNumeric(val) {
  if (val === null || val === undefined || val === '') return null;
  if (Array.isArray(val)) return extractNumeric(val[0]);
  const num = typeof val === 'number' ? val : Number(val);
  return Number.isFinite(num) ? num : null;
}

function isTruthyFlag(val) {
  if (val === 1 || val === true || val === '1') return true;
  if (typeof val === 'string' && (val === '1.0' || val.toLowerCase() === 'true')) return true;
  if (Array.isArray(val)) return isTruthyFlag(val[0]);
  return false;
}

function isSelectedCompany(comp, tabSelected) {
  if (isTruthyFlag(comp['Subject Company'])) return false;
  if (isTruthyFlag(comp.Outlier)) return false;
  if (!isTruthyFlag(comp.Active)) return false;
  // Comps tab: checked = Active + Multiples; Industry tab: checked = Active
  if (tabSelected === 'comps') return isTruthyFlag(comp.Multiples);
  return true;
}

function percentile(values, pct) {
  if (!values.length) return '';
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 1) return sorted[0];
  const index = (pct / 100) * (sorted.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sorted[lower];
  const weight = index - lower;
  return sorted[lower] * (1 - weight) + sorted[upper] * weight;
}

function buildMetricStats(values) {
  if (!values.length) {
    return {
      max: '',
      '95%': '',
      '90%': '',
      '75%': '',
      '50%': '',
      '25%': '',
      '10%': '',
      '5%': '',
      min: '',
      mean: '',
    };
  }

  return {
    max: Math.max(...values),
    '95%': percentile(values, 95),
    '90%': percentile(values, 90),
    '75%': percentile(values, 75),
    '50%': percentile(values, 50),
    '25%': percentile(values, 25),
    '10%': percentile(values, 10),
    '5%': percentile(values, 5),
    min: Math.min(...values),
    mean: values.reduce((sum, val) => sum + val, 0) / values.length,
  };
}

/**
 * Build summary rows from currently selected comps for the active tab.
 * All columns use the same selected-company set.
 * Row order: Maximum, 95th, 90th, 75th, Median, 25th, 10th, 5th, Minimum, Average.
 */
export default function buildCompSummaries(compData = [], tabSelected = 'comps') {
  const metrics = {};
  const metricPrefixes = [...MULTIPLE_PREFIXES, ...INDUSTRY_PREFIXES, ...VALUE_PREFIXES];

  compData.forEach((comp) => {
    if (!comp || typeof comp !== 'object') return;
    if (!isSelectedCompany(comp, tabSelected)) return;

    Object.keys(comp).forEach((key) => {
      if (!startsWithAny(key, SUMMARIZE_PREFIXES)) return;
      if (!startsWithAny(key, metricPrefixes)) return;

      const num = extractNumeric(comp[key]);
      if (num === null) return;

      if (!metrics[key]) metrics[key] = [];
      metrics[key].push(num);
    });
  });

  const summariesByMetric = {};
  Object.keys(metrics).forEach((key) => {
    summariesByMetric[key] = buildMetricStats(metrics[key]);
  });

  return SUMMARY_ROW_KEYS.map((rowKey) => {
    const row = { metric: rowKey };
    Object.keys(summariesByMetric).forEach((metricKey) => {
      row[metricKey] = summariesByMetric[metricKey][rowKey];
    });
    return row;
  });
}
